import React, { useEffect } from "react";
import Header from "../home/header";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Login from "../home/login_form";
import feature from "../../img/customer.svg";
import Loading from "../home/Loading";
import UTILS from "../../utils/commons";
import { ApiClient } from "../../utils/api-client";
import { AxiosError } from "axios";
import { X } from "lucide-react";
import TransactionItem from "../transactions/transaction_item";

const CustomerDetail = ( props ) => {
  let navigate = useNavigate();
  var loginState = JSON.parse( window.sessionStorage.getItem( "loginState" ) );
  var { id } = useParams();
  const apiClient = new ApiClient( loginState ? loginState.accessToken : null );
  const [ customer, setCustomer ] = React.useState( null );
  const [ loading, setLoading ] = React.useState( false );
  const [ recentTransactions, setRecentTransactions ] = React.useState( [] );
  const [ debts, setDebts ] = React.useState( { debt: "N/A", totalCredit: "N/A", totalPaid: "N/A" } );

  const getRecentTransactions = async ( customerId ) => {
    try {
      const req = await apiClient.get( `/transactions`, {
        params: {
          customerId: customerId,
          limit: 10
        }
      } );
      if ( req.status === 200 ) {
        setRecentTransactions( req.data );
      }
      else {
        setRecentTransactions( [] );
      }
    }
    catch ( e ) {
      console.log( "Error fetching transaction detail", e );
      setRecentTransactions( [] );
      if ( e instanceof AxiosError && e.response && e.response.status === 401 ) {
        UTILS.signout();
        navigate( `/signin`, { replace: true, target: `/customers/view/${id}` } );
      }
    }
    finally {
      setLoading( false );
    }
  }

  const getCustomerDetails = async () => {
    try {
      const response = await apiClient.get( "/customers/" + id );
      if ( response.status === 200 ) {
        setCustomer( response.data );
      }
      else {
        setCustomer( null );
      }
    }
    catch ( e ) {
      console.log( "Error fetching customer detail", e );
      setCustomer( null );
      if ( e instanceof AxiosError && e.response && e.response.status === 401 ) {
        UTILS.signout();
        navigate( `/signin`, { replace: true, target: `/customers/view/${id}` } );
      }
    }
  }
  const getCustomerDebts = async ( customerId ) => {
    try {
      const response = await apiClient.get( `/payments/customer-debt/${customerId}` );
      if ( response.status === 200 ) {
        setDebts( response.data );
      }
      else {
        setDebts( {
          debt: 'Failed to fetch',
          totalCredit: "Failed to fetch",
          totalPaid: "Failed to fetch"
        } );
      }
    }
    catch ( e ) {
      console.log( "Error fetching customer debts", e );
      if ( e instanceof AxiosError && e.response && e.response.status === 401 ) {
        UTILS.signout();
        navigate( `/signin`, { replace: true, target: `/customers/view/${customerId}` } );
      }
      setDebts( {
        debt: 'Failed to fetch',
        totalCredit: "Failed to fetch",
        totalPaid: "Failed to fetch"
      } );
    }
  }
  const getData = async () => {
    setLoading( true );
    await Promise.all( [ getCustomerDetails(), getRecentTransactions( id ), getCustomerDebts(id) ] );
    setLoading( false );
  }
  useEffect( () => {
    getData();
  }, [] )
  return ( <>
    <Header auth={loginState} />
    <section className="container mt-8">
      <div className="w-full mb-4 flex justify-between items-start">
        <div>
          <p className="text-sm uppercase tracking-wide text-gray-500">Customer Profile</p>
          <p className="text-2xl font-semibold text-gray-800">Customer Details</p>
        </div>
        <button className="p-1 rounded-md hover:bg-gray-100 transition-colors" onClick={() => { navigate( -1, { replace: true } ) }}><X className="text-primary-500 hover:text-secondary-500" /></button>
      </div>
      {loading ? <Loading className="animate-spin text-primary-500" /> :
        customer ?
          <>
            <div className="w-full rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-4 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                <div className="flex flex-col sm:flex-row lg:flex-col items-center lg:items-start gap-4">
                  <img id="preview" alt={customer?.name} src={customer?.customerImage ? customer?.customerImage : feature} className="h-28 w-28 rounded-full object-cover border-2 border-primary-200 shadow-sm mx-auto" />
                  <div className="text-center sm:text-left lg:text-left">
                    <p className="text-xl font-semibold text-gray-800">{customer?.name}</p>
                    <p className="text-sm text-gray-500 mt-1">{customer?.email || 'No email provided'}</p>
                    <p className="inline-block mt-3 text-xs font-semibold uppercase tracking-wide text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">{customer?.region || 'No region'}</p>
                  </div>
                </div>

                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Customer Status</p>
                    <p className="text-secondary-700 font-medium mt-1">{UTILS.activeStatusBadge( customer.isActive )}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Phone Number</p>
                    <p className="text-secondary-700 font-medium mt-1">{customer?.phone}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Address</p>
                    <p className="text-secondary-700 font-medium mt-1">{`${customer?.address || ''}${customer?.region ? `, ${ customer.region }` : ''}`}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Handled by</p>
                    <p className="text-secondary-700 font-medium mt-1">
                      {customer?.manager?.id ? <span className="hover:underline" onClick={() => navigate(`/staff/view/${customer.manager.id}`, { replace: false, state: customer?.manager })}>{`${customer?.manager?.firstName || ''} ${customer?.manager?.lastName || ''}`.trim() || 'N/A'}</span> : (`${customer?.manager?.firstName || ''} ${customer?.manager?.lastName || ''}`.trim() || 'N/A')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Purchased (TZS)</p>
                  <p className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( recentTransactions.reduce( ( sum, tx ) => sum + Number( tx.totalAmount ), 0 ) )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Credit (TZS)</p>
                  <p className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( debts.totalCredit )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Paid (TZS)</p>
                  <p className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( debts.totalPaid )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Credit Balance (TZS)</p>
                  <p className="text-red-600 font-semibold mt-1">{UTILS.thousandSeparator( debts.debt )}</p>
                </div>
              </div>
            </div>
              <div className="text-2xl mt-8 mb-2">Recent Transactions</div>
              {recentTransactions.length === 0 ? <div className="mb-2 border border-dashed rounded-md px-3 py-6 text-sm text-gray-500 text-center bg-gray-50">No recent transactions</div> :
                <>
                  <div className="grid md:grid-cols-6 bg-gray-100 p-3 text-xs uppercase tracking-wide font-semibold text-gray-600 rounded-t-md border border-gray-200">
                    <span>Date</span>
                    <span>Type</span>
                    <span>Customer</span>
                    <span>Amount (TZS)</span>
                    <span className="text-start">Document</span>
                    <span>Action</span>
                  </div>
                  <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden">
                    {recentTransactions.map( ( transaction, idx ) => {
                      return <TransactionItem className={`py-1 ${idx % 2 === 0 ? "bg-white" : "bg-gray-100"}`} data={transaction} key={transaction.id} onDelete={() => {}} onClick={() => { navigate( "/transactions/view/" + transaction.id, { replace: false, state: transaction } ) }} onEdit={() => { navigate( "/transactions/edit/" + transaction.id, { replace: false, state: transaction } ) }} />
                    } )}
                  </div>
                </>
              }
            </> : <span className="text-center w-full">Could not load customer data.</span>
      }
          </section>
    </>
    )

}
    export default CustomerDetail;